import {
    faEllipsisVertical,
    faExternalLink,
    faPlus,
    faInbox,
    faTag as faTagOutline,
    faBarsFilter,
    faRobot,
    faSpinnerThird,
    faTags,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import Button from '../../../core/components/Button.tsx';
import { USER_PROFILE_PATH } from '../../../users/constants.ts';
import { canManageSmartInbox } from '../../../users/utils.ts';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import EmptyStage from '../../../core/components/EmptyStage.tsx';
import Filter from '../../../core/components/Filter.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import OrderByDropdown from '../../../core/components/OrderByDropdown.tsx';
import Page from '../../../core/components/Page';
import RecordTable, { RecordTableColumn, ROW_CONTROLS_KEY } from '../../../core/components/RecordTable.tsx';
import SearchField from '../../../core/components/SearchField.tsx';
import { RowControlIconButton } from '../../../core/components/Table.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import { SAD } from '../../../core/components/worki/Pose.tsx';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import { TabFilter } from '../../../document/pages/Overview/internal/FilterBar';
import { ASSISTANT_PATH } from '../../assistants/constants.ts';
import AddMailboxModal from '../components/AddMailboxModal.tsx';
import AdminConsentSetupCard from '../components/AdminConsentSetupCard.tsx';
import CategoryCatalogTab, { CategoryCatalogTabHandle } from '../components/CategoryCatalogTab.tsx';
import { MAILBOX_DETAIL_PATH, OVERVIEW_PATH, CATEGORY_CATALOG_PATH } from '../constants.ts';
import useMailboxData, { MailboxRow } from '../hooks/useMailboxData.ts';
import useMailboxFilters, { NO_AGENT_VALUE } from '../hooks/useMailboxFilters.ts';

const AddIcon = withIcon(faPlus);
const ExternalLinkIcon = withIcon(faExternalLink);
const EllipsisIcon = withIcon(faEllipsisVertical);
const FilterListIcon = withIcon(faBarsFilter);
const InboxesIcon = withIcon(faInbox);
const CategoryCatalogIcon = withIcon(faTags);
const DotIcon = withIcon(faCircle);
const AgentIcon = withIcon(faRobot);
const SpinnerIcon = withIcon(faSpinnerThird);

const OverviewPage = () => {
    const { t } = useTranslation('mailboxes');
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    const location = useLocation();
    const isSharedInboxAdminConsentGranted = user?.customer?.sharedInboxAdminConsent?.isGranted;
    const isPersonalInboxAdminConsentGranted = user?.customer?.personalInboxAdminConsent?.isGranted;
    // seeing the page only needs one of the two consents granted, but actually connecting a
    // mailbox from here (shared, direct; personal, an invite reminder) needs the shared one —
    // personal mailboxes are otherwise connected by the person themselves from their profile page
    const hasAnyAdminConsent = isSharedInboxAdminConsentGranted || isPersonalInboxAdminConsentGranted;
    const canManageShared = canManageSmartInbox(user);
    const canConnectMailbox = isSharedInboxAdminConsentGranted && canManageShared;

    const activeTab = location.pathname === CATEGORY_CATALOG_PATH ? 'categoryCatalog' : 'overview';
    const fromAssistant = (location.state as { fromAssistant?: { id: string; name: string } } | null)?.fromAssistant;

    const categoryCatalogTabRef = useRef<CategoryCatalogTabHandle>(null);

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [rowToDisconnect, setRowToDisconnect] = useState<MailboxRow | null>(null);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [pendingActionId, setPendingActionId] = useState<string | null>(null);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [bulkAction, setBulkAction] = useState<'pause' | 'resume' | null>(null);
    const [bulkDisconnectConfirmOpen, setBulkDisconnectConfirmOpen] = useState(false);
    const [bulkDisconnecting, setBulkDisconnecting] = useState(false);

    const { rows, loading, updateSharedMailbox, deleteMailbox, setupPersonalMailbox } = useMailboxData();
    const hasMailboxes = rows.length > 0;
    const [isCatalogEmpty, setIsCatalogEmpty] = useState(false);

    const agentOptions = useMemo(() => {
        const names = new Set<string>();
        rows.forEach((row) => row.agentNames.forEach((name) => names.add(name)));
        return [
            ...[...names].sort().map((name) => ({ value: name, label: name })),
            { value: NO_AGENT_VALUE, label: t('overview.filters.noAgent'), separatorBefore: names.size > 0 },
        ];
    }, [rows, t]);

    const {
        search,
        setSearch,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        showFilters,
        setShowFilters,
        typeFilter,
        setTypeFilter,
        statusFilter,
        setStatusFilter,
        agentFilter,
        setAgentFilter,
        hasActiveFilters,
        visibleRows,
    } = useMailboxFilters(rows);

    const clearFilters = () => {
        setSearch('');
        setTypeFilter([]);
        setStatusFilter([]);
        setAgentFilter([]);
    };

    const setMailboxEnabled = (row: MailboxRow, enabled: boolean) =>
        row.type === 'shared'
            ? updateSharedMailbox({ variables: { id: row.id, name: row.name, enabled } })
            : setupPersonalMailbox({ variables: { integrationId: row.id, enabled } });

    const handleTogglePause = async (row: MailboxRow) => {
        setPendingActionId(row.id);
        try {
            await setMailboxEnabled(row, !row.enabled);
            publishToast({
                description: row.enabled ? t('overview.pauseSuccess') : t('overview.resumeSuccess'),
                status: 'success',
            });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('overview.actionError'), status: 'error' });
        } finally {
            setPendingActionId(null);
        }
    };

    const selectedRows = rows.filter((row) => selectedIds.includes(row.id));

    const handleBulkPause = async () => {
        const targets = selectedRows.filter((row) => row.enabled);
        if (targets.length === 0) return;
        setBulkAction('pause');
        try {
            await Promise.all(targets.map((row) => setMailboxEnabled(row, false)));
            publishToast({
                description: t('overview.batchActions.pauseSuccess', { count: targets.length }),
                status: 'success',
            });
            setSelectedIds([]);
        } catch (error) {
            console.error(error);
            publishToast({ description: t('overview.actionError'), status: 'error' });
        } finally {
            setBulkAction(null);
        }
    };

    const handleBulkResume = async () => {
        const targets = selectedRows.filter((row) => !row.enabled);
        if (targets.length === 0) return;
        setBulkAction('resume');
        try {
            await Promise.all(targets.map((row) => setMailboxEnabled(row, true)));
            publishToast({
                description: t('overview.batchActions.resumeSuccess', { count: targets.length }),
                status: 'success',
            });
            setSelectedIds([]);
        } catch (error) {
            console.error(error);
            publishToast({ description: t('overview.actionError'), status: 'error' });
        } finally {
            setBulkAction(null);
        }
    };

    const handleBulkDisconnect = async () => {
        setBulkDisconnecting(true);
        try {
            await Promise.all(selectedRows.map((row) => deleteMailbox({ variables: { id: row.id } })));
            publishToast({
                description: t('overview.batchActions.disconnectSuccess', { count: selectedRows.length }),
                status: 'success',
            });
            setSelectedIds([]);
            setBulkDisconnectConfirmOpen(false);
        } catch (error) {
            console.error(error);
            publishToast({ description: t('overview.actionError'), status: 'error' });
        } finally {
            setBulkDisconnecting(false);
        }
    };

    const defaultTabFor = (type: MailboxRow['type']) => (type === 'shared' ? 'categories' : 'activity');

    const detailUrl = (row: MailboxRow) =>
        url(MAILBOX_DETAIL_PATH, { id: row.id, tab: defaultTabFor(row.type) }, { search: { type: row.type } });

    const tabsNav = (
        <>
            <TabFilter className="py-4.5" to={OVERVIEW_PATH} active={activeTab === 'overview'}>
                <InboxesIcon />
                {t('overview.tabs.overview')}
            </TabFilter>
            {canManageShared && (
                <TabFilter className="py-4.5" to={CATEGORY_CATALOG_PATH} active={activeTab === 'categoryCatalog'}>
                    <CategoryCatalogIcon />
                    {t('overview.tabs.categoryCatalog')}
                </TabFilter>
            )}
        </>
    );

    const handleDisconnect = async () => {
        if (!rowToDisconnect) return;
        setPendingActionId(rowToDisconnect.id);
        try {
            await deleteMailbox({ variables: { id: rowToDisconnect.id } });
            publishToast({ description: t('overview.disconnectSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('overview.actionError'), status: 'error' });
        } finally {
            setPendingActionId(null);
            setRowToDisconnect(null);
        }
    };

    const records = useMemo(() => visibleRows.map((row) => ({ ...row, key: row.id })), [visibleRows]);

    const columns: RecordTableColumn[] = useMemo(
        () => [
            {
                key: 'mailbox',
                label: t('overview.columns.mailbox'),
                render: (record) => (
                    <Link className="block truncate font-medium" to={detailUrl(record as unknown as MailboxRow)}>
                        {record.mailInbox}
                    </Link>
                ),
            },
            {
                key: 'type',
                label: t('overview.columns.type'),
                className: 'whitespace-nowrap',
                render: (record) => (record.type === 'shared' ? t('overview.typeShared') : t('overview.typePersonal')),
            },
            {
                key: 'routing',
                label: t('overview.columns.routing'),
                className: 'whitespace-nowrap',
                render: (record) =>
                    record.rulesCount === null ? (
                        <span className="text-content-weak">{t('overview.notAdded')}</span>
                    ) : (
                        <span>
                            <Trans
                                ns="mailboxes"
                                i18nKey="overview.rulesCount"
                                count={record.rulesCount}
                                values={{ count: record.rulesCount }}
                                components={{ bold: <span className="font-medium" /> }}
                            >
                                <span className="font-medium">{record.rulesCount}</span> category
                            </Trans>
                        </span>
                    ),
            },
            {
                key: 'status',
                label: t('overview.columns.status'),
                className: 'whitespace-nowrap',
                render: (record) => (
                    <span
                        className={classnames(
                            'text-content-strong font-medium text-sm items-center border-[1px] px-2 py-0.5 rounded',
                            record.enabled
                                ? 'text-content-brand-medium border-line-success-weak bg-surface-success-weak'
                                : 'text-content-warning-default border-line-warning-weak bg-surface-warning-weak'
                        )}
                    >
                        <DotIcon
                            className={classnames(
                                'text-[10px] pr-2 pb-[1px]',
                                record.enabled ? 'text-content-success-weak' : 'text-content-warning-weak'
                            )}
                        />
                        {record.enabled ? t('overview.statusActive') : t('overview.statusPaused')}
                    </span>
                ),
            },
            {
                key: 'agent',
                label: t('overview.columns.agent'),
                className: 'whitespace-nowrap',
                render: (record) =>
                    record.agentNames.length > 0 ? (
                        <span className="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-surface-agent-weak text-content-agent-default flex-none w-fit">
                            <AgentIcon className="text-[10px]" />
                            {record.agentNames[0]}
                        </span>
                    ) : (
                        '—'
                    ),
            },
            {
                key: ROW_CONTROLS_KEY,
                label: '',
                render: (record) => (
                    <div className="flex gap-2">
                        <Tooltip content={t('overview.openInNewTab')} side="bottom">
                            <RowControlIconButton asChild>
                                <Link
                                    to={detailUrl(record as unknown as MailboxRow)}
                                    target="_blank"
                                    aria-label={t('overview.openInNewTab')}
                                >
                                    <ExternalLinkIcon />
                                </Link>
                            </RowControlIconButton>
                        </Tooltip>
                        <DropdownMenu
                            open={openMenuId === record.id}
                            onOpenChange={(open) => setOpenMenuId(open ? record.id : null)}
                        >
                            <DropdownMenu.Trigger asChild>
                                <RowControlIconButton
                                    disabled={pendingActionId === record.id}
                                    aria-label={t('overview.moreActions')}
                                >
                                    <EllipsisIcon />
                                </RowControlIconButton>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content align="end">
                                <DropdownMenu.Item onClick={() => handleTogglePause(record as unknown as MailboxRow)}>
                                    {record.enabled ? t('overview.actionPause') : t('overview.actionResume')}
                                </DropdownMenu.Item>
                                {canManageShared && (
                                    <>
                                        <DropdownMenu.Separator />
                                        <DropdownMenu.Item
                                            danger
                                            onClick={() => setRowToDisconnect(record as unknown as MailboxRow)}
                                        >
                                            {t('overview.actionDisconnect')}
                                        </DropdownMenu.Item>
                                    </>
                                )}
                            </DropdownMenu.Content>
                        </DropdownMenu>
                    </div>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, openMenuId, pendingActionId, canManageShared]
    );

    return (
        <Layout>
            <Page>
                <Page.Header className="justify-between">
                    <div className="flex min-w-0 items-center">
                        {fromAssistant && (
                            <Page.HeaderBreadcrumb to={url(ASSISTANT_PATH, { channelId: fromAssistant.id })}>
                                {fromAssistant.name}
                            </Page.HeaderBreadcrumb>
                        )}
                        <Page.HeaderTitle>{t('overview.title')}</Page.HeaderTitle>
                    </div>
                    {activeTab === 'overview' && canManageShared && hasMailboxes && (
                        <Page.HeaderButton disabled={!canConnectMailbox} onClick={() => setIsAddModalVisible(true)}>
                            <AddIcon />
                            {t('overview.addMailbox')}
                        </Page.HeaderButton>
                    )}
                    {activeTab === 'categoryCatalog' && !isCatalogEmpty && (
                        <Page.HeaderButton onClick={() => categoryCatalogTabRef.current?.openCreateModal()}>
                            <AddIcon />
                            {t('detail.categoriesTab.addCategory')}
                        </Page.HeaderButton>
                    )}
                </Page.Header>

                {activeTab === 'overview' && (
                    <>
                        {hasAnyAdminConsent && (
                            <Page.Header className="py-0 justify-between" primary={false}>
                                <div className="flex h-full">{tabsNav}</div>

                                {hasMailboxes && (
                                    <div className="flex items-center gap-2 py-2.5">
                                        <SearchField
                                            placeholder={t('overview.searchPlaceholder')}
                                            value={search}
                                            onValueChange={setSearch}
                                        />

                                        <button
                                            type="button"
                                            className={classnames(
                                                'flex items-center gap-2 h-8 px-3 rounded cursor-pointer text-sm font-medium text-content-strong hover:bg-surface-strong',
                                                (showFilters || hasActiveFilters) &&
                                                    '!bg-surface-brand-medium-default !text-content-brand-strong'
                                            )}
                                            onClick={() => setShowFilters(!showFilters)}
                                        >
                                            <FilterListIcon />
                                            {t('overview.filters.toggleLabel')}
                                            {hasActiveFilters && (
                                                <span>
                                                    ({typeFilter.length + statusFilter.length + agentFilter.length})
                                                </span>
                                            )}
                                        </button>

                                        <OrderByDropdown
                                            options={[
                                                { value: 'type', label: t('overview.columns.type') },
                                                { value: 'mailbox', label: t('overview.columns.mailbox') },
                                            ]}
                                            selected={sortDirection === 'desc' ? `-${sortBy}` : sortBy}
                                            defaultValue="mailbox"
                                            onSelectedChange={(value: string) => {
                                                const desc = value.startsWith('-');
                                                setSortBy((desc ? value.slice(1) : value) as 'mailbox' | 'type');
                                                setSortDirection(desc ? 'desc' : 'asc');
                                            }}
                                            inline
                                        />
                                    </div>
                                )}
                            </Page.Header>
                        )}

                        {showFilters && hasMailboxes && (
                            <div className="flex items-center gap-3 px-7 py-2 border-b border-solid border-line-weak bg-surface-medium flex-none">
                                <Filter
                                    label={t('overview.filters.type.label')}
                                    allLabel={t('overview.filters.type.all')}
                                    options={[
                                        { value: 'shared', label: t('overview.typeShared') },
                                        { value: 'personal', label: t('overview.typePersonal') },
                                    ]}
                                    selected={typeFilter}
                                    onSelectedChange={setTypeFilter}
                                />
                                <Filter
                                    label={t('overview.filters.status.label')}
                                    allLabel={t('overview.filters.status.all')}
                                    options={[
                                        { value: 'active', label: t('overview.statusActive') },
                                        { value: 'paused', label: t('overview.statusPaused') },
                                    ]}
                                    renderOption={(option) => (
                                        <span className="flex items-center gap-2">
                                            <DotIcon
                                                className={classnames(
                                                    'text-xs',
                                                    option.value === 'active'
                                                        ? 'text-content-success-weak'
                                                        : 'text-content-warning-weak'
                                                )}
                                            />
                                            {option.label}
                                        </span>
                                    )}
                                    selected={statusFilter}
                                    onSelectedChange={setStatusFilter}
                                />
                                <Filter
                                    label={t('overview.filters.agent.label')}
                                    allLabel={t('overview.filters.agent.all')}
                                    options={agentOptions}
                                    selected={agentFilter}
                                    onSelectedChange={setAgentFilter}
                                    multiple
                                    showSearch
                                />
                            </div>
                        )}

                        <Page.Content lowered className="relative flex flex-col gap-4">
                            {loading && rows.length === 0 ? (
                                <div className="flex items-center justify-center py-16">
                                    <SpinnerIcon spin className="text-3xl text-content-weak" />
                                </div>
                            ) : rows.length === 0 && !canManageShared ? (
                                <EmptyStage
                                    size={88}
                                    title={t('overview.emptyTitle')}
                                    description={t('overview.personalEmptyDescription')}
                                    action={
                                        <Button asChild variant="primary">
                                            <Link to={USER_PROFILE_PATH}>{t('overview.goToProfile')}</Link>
                                        </Button>
                                    }
                                />
                            ) : rows.length === 0 && !canConnectMailbox ? (
                                // "connect your first mailbox" only once the shared consent allows connecting one
                                <AdminConsentSetupCard />
                            ) : rows.length === 0 ? (
                                <EmptyStage
                                    size={88}
                                    title={t('overview.emptyTitle')}
                                    description={t('overview.emptyDescription')}
                                    action={
                                        <Button variant="primary" onClick={() => setIsAddModalVisible(true)}>
                                            {t('overview.addMailbox')}
                                        </Button>
                                    }
                                />
                            ) : visibleRows.length === 0 ? (
                                <EmptyStage
                                    size={88}
                                    pose={SAD}
                                    title={t('overview.noResultsTitle')}
                                    description={t('overview.noResultsDescription')}
                                    action={
                                        <Button variant="outline" onClick={clearFilters}>
                                            {t('overview.clearFilters')}
                                        </Button>
                                    }
                                />
                            ) : (
                                <RecordTable
                                    columns={columns}
                                    records={records}
                                    loading={loading}
                                    selectable
                                    selectedRowKeys={selectedIds}
                                    onSelectedRowKeysChange={setSelectedIds}
                                />
                            )}

                            {selectedIds.length > 0 && (
                                <BulkActionBar
                                    t={t}
                                    count={selectedIds.length}
                                    bulkAction={bulkAction}
                                    canPause={selectedRows.some((row) => row.enabled)}
                                    canResume={selectedRows.some((row) => !row.enabled)}
                                    onPause={handleBulkPause}
                                    onResume={handleBulkResume}
                                    onDisconnect={
                                        canManageShared ? () => setBulkDisconnectConfirmOpen(true) : undefined
                                    }
                                />
                            )}
                        </Page.Content>
                    </>
                )}

                {activeTab === 'categoryCatalog' && (
                    <CategoryCatalogTab
                        ref={categoryCatalogTabRef}
                        tabsNav={tabsNav}
                        onEmptyChange={setIsCatalogEmpty}
                    />
                )}
            </Page>

            {isAddModalVisible && <AddMailboxModal onClose={() => setIsAddModalVisible(false)} />}

            {rowToDisconnect && (
                <ConfirmModal
                    title={t('overview.disconnectConfirmTitle')}
                    onConfirm={handleDisconnect}
                    onCancel={() => setRowToDisconnect(null)}
                    confirmDisabled={pendingActionId === rowToDisconnect.id}
                    labelConfirm={t('overview.disconnect')}
                    danger
                >
                    {t('overview.disconnectConfirmMessage', { mailbox: rowToDisconnect.mailInbox })}
                </ConfirmModal>
            )}

            {bulkDisconnectConfirmOpen && (
                <ConfirmModal
                    title={t('overview.batchActions.disconnectConfirmTitle')}
                    onConfirm={handleBulkDisconnect}
                    onCancel={() => setBulkDisconnectConfirmOpen(false)}
                    confirmDisabled={bulkDisconnecting}
                    labelConfirm={t('overview.disconnect')}
                    danger
                >
                    {t('overview.batchActions.disconnectConfirmMessage', { count: selectedRows.length })}
                </ConfirmModal>
            )}
        </Layout>
    );
};

// dot + colored label, no icon/button chrome — matches the same status-dot language as the
// table's own status badges (warning=paused, success=active, danger=disconnect)
const BulkActionButton = ({
    dotClassName,
    textClassName,
    disabled,
    onClick,
    children,
}: {
    dotClassName: string;
    textClassName: string;
    disabled?: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) => (
    <button
        type="button"
        className={classnames(
            'flex flex-none cursor-pointer items-center gap-2 rounded-lg border-none bg-transparent px-3 py-1.5 text-sm font-medium hover:bg-surface-strong disabled:pointer-events-none disabled:opacity-50',
            textClassName
        )}
        disabled={disabled}
        onClick={onClick}
    >
        <span className={classnames('h-2 w-2 flex-none rounded-full', dotClassName)} />
        {children}
    </button>
);

const BulkActionDivider = () => <div className="mx-1 h-6 w-px flex-none bg-surface-strong" />;

const BulkActionBar = ({
    t,
    count,
    bulkAction,
    canPause,
    canResume,
    onPause,
    onResume,
    onDisconnect,
}: {
    t: (key: string, options?: any) => string;
    count: number;
    bulkAction: 'pause' | 'resume' | null;
    canPause: boolean;
    canResume: boolean;
    onPause: () => void;
    onResume: () => void;
    onDisconnect?: () => void;
}) => (
    <div className="sticky bottom-0 left-0 flex w-full justify-center pointer-events-none">
        <div className="flex flex-wrap items-center justify-center gap-1 py-2 pl-4 pr-3 min-h-14 rounded-xl bg-surface-weak shadow-xl text-content-strong pointer-events-auto">
            <div className="mr-2 flex-none text-sm">
                <strong className="font-medium">{count}</strong> {t('overview.batchActions.selected', { count })}
            </div>
            {canPause && (
                <BulkActionButton
                    dotClassName="bg-content-warning-weak"
                    textClassName="text-content-warning-default"
                    disabled={bulkAction === 'pause'}
                    onClick={onPause}
                >
                    {t('overview.batchActions.pauseButton')}
                </BulkActionButton>
            )}
            {canPause && canResume && <BulkActionDivider />}
            {canResume && (
                <BulkActionButton
                    dotClassName="bg-content-success-weak"
                    textClassName="text-content-brand-medium"
                    disabled={bulkAction === 'resume'}
                    onClick={onResume}
                >
                    {t('overview.batchActions.resumeButton')}
                </BulkActionButton>
            )}
            {(canPause || canResume) && onDisconnect && <BulkActionDivider />}
            {onDisconnect && (
                <BulkActionButton
                    dotClassName="bg-content-danger-medium"
                    textClassName="text-content-danger-medium"
                    onClick={onDisconnect}
                >
                    {t('overview.batchActions.disconnectButton')}
                </BulkActionButton>
            )}
        </div>
    </div>
);

export default OverviewPage;
